import React from 'react';
import { Box, Container, Grid, Typography, Card, CardContent, useTheme, Button } from '@mui/material';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import SectionHeader from '../SectionHeader';
import EnhancedEncryptionIcon from '@mui/icons-material/EnhancedEncryption';
import LockPersonIcon from '@mui/icons-material/LockPerson';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import StoreButtons from '../StoreButtons';

const SecuritySection = () => {
  const theme = useTheme();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const securityFeatures = [
    {
      icon: <EnhancedEncryptionIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />,
      title: 'Сквозное шифрование',
      description: 'Все разговоры и данные пациентов зашифрованы, обеспечивая конфиденциальность медицинской информации.',
    },
    {
      icon: <LockPersonIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />,
      title: 'Защищенная аутентификация',
      description: 'Надежные процессы входа для проверки личности пользователей и предотвращения несанкционированного доступа.',
    },
    {
      icon: <VerifiedUserIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />,
      title: 'Защита данных',
      description: 'Личная и медицинская информация хранится в безопасности, доступ к ней строго ограничен.',
    },
    {
      icon: <WifiOffIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />,
      title: 'Безопасность офлайн',
      description: 'Надежные меры безопасности при работе офлайн, защита всей сохраненной информации на устройстве.',
    },
  ];

  return (
    <Box
      id="security"
      sx={{
        py: 10,
        backgroundColor: 'rgba(46, 80, 119, 0.05)',
      }}
      ref={ref}
    >
      <Container maxWidth="lg">
        <SectionHeader
          title="Безопасность и защита данных"
          subtitle="ДокМедЧат построен с учетом высоких стандартов безопасности для защиты персональных и медицинских данных."
        />

        <Grid container spacing={4} sx={{ mt: 2 }}>
          {securityFeatures.map((feature, index) => (
            <Grid item xs={12} sm={6} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    boxShadow: '0 5px 15px rgba(0, 0, 0, 0.08)',
                    borderLeft: '4px solid',
                    borderColor: 'primary.main',
                    transition: 'transform 0.3s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.12)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 3, display: 'flex', width: '100%' }}>
                    <Box
                      sx={{
                        mr: 3,
                        mt: 1,
                        backgroundColor: 'rgba(46, 80, 119, 0.1)',
                        borderRadius: '50%',
                        width: 60,
                        height: 60,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Box>
                      <Typography variant="h6" component="h3" sx={{ mb: 1, fontWeight: 600 }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        {feature.description}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ mt: 8 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -30 }}
                transition={{ duration: 0.8 }}
              >
                <Typography variant="h4" sx={{ mb: 3, color: 'primary.main', fontWeight: 600 }}>
                  Соответствие стандартам защиты
                </Typography>
                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', mb: 3 }}>
                  ДокМедЧат обеспечивает безопасный способ для медицинских учреждений поддерживать регулярный контакт с пациентами, предлагать удаленные консультации.
                </Typography>
                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', mb: 3 }}>
                  Благодаря шифрованию, гарантирующему конфиденциальность медицинской информации, приложение позволяет эффективно общаться врачам и пациентам.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  sx={{ mt: 2, mb: 4, fontWeight: 600 }}
                >
                  Узнать больше о безопасности
                </Button>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={inView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.7 }}
              >
                <Box
                  sx={{
                    p: 4,
                    backgroundColor: theme.palette.primary.main,
                    color: 'white',
                    borderRadius: 3,
                    boxShadow: '0 10px 30px rgba(46, 80, 119, 0.3)',
                  }}
                >
                  <Typography variant="h5" sx={{ mb: 3, fontWeight: 500 }}>
                    Почему безопасность важна
                  </Typography>
                  <Typography variant="body1" paragraph sx={{ opacity: 0.9, mb: 2 }}>
                    В медицинской сфере защита данных — критический аспект доверия между клиникой и пациентом.
                  </Typography>
                  <Typography variant="body1" paragraph sx={{ opacity: 0.9, mb: 2 }}>
                    ДокМедЧат обеспечивает:
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, mb: 3 }}>
                    <Typography component="li" variant="body1" sx={{ opacity: 0.9, mb: 1 }}>
                      Защиту врачей от раскрытия личных данных
                    </Typography>
                    <Typography component="li" variant="body1" sx={{ opacity: 0.9, mb: 1 }}>
                      Конфиденциальность медицинской информации
                    </Typography>
                    <Typography component="li" variant="body1" sx={{ opacity: 0.9, mb: 1 }}>
                      Соответствие нормам хранения данных
                    </Typography>
                    <Typography component="li" variant="body1" sx={{ opacity: 0.9 }}>
                      Контроль доступа к информации
                    </Typography>
                  </Box>
                  <StoreButtons direction="column" />
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default SecuritySection; 